import { useMemo } from 'react';
import type Highcharts from 'highcharts';
import type { DirectShearReading } from '../../../../types/directShearReading';
import type { AnalysisRanges } from '../analysisTypes';
import type { MarkerRow } from '../chartConfig';
import { baseChartOptions, buildMarkerSeries } from '../chartConfig';
import { symmetricAxisBounds } from '../../../../utils/chartUtils';

interface DisplacementDilationChartProps {
  readings: DirectShearReading[];
  title: string;
  yLabel: string;
  yField: keyof DirectShearReading;
  color: string;
  ranges: AnalysisRanges;
  peakRow: MarkerRow;
  residualRow: MarkerRow;
}

export function useDisplacementDilationChartOptions({
  readings,
  title,
  yLabel,
  yField,
  color,
  peakRow,
  residualRow,
}: DisplacementDilationChartProps): Highcharts.Options {
  return useMemo((): Highcharts.Options => {
    const data = readings
      .filter((r) => r.HorizontalDisplacementCumulative != null && r[yField] != null)
      .map((r) => [
        r.HorizontalDisplacementCumulative as number,
        r[yField] as number,
      ]);

    const markers = buildMarkerSeries(peakRow, residualRow, yField);

    // Dilation only: mirror the Y axis around zero so 0° is the visual centre.
    const isDilation = yField === 'DilationAngle';
    const symBounds = isDilation
      ? symmetricAxisBounds(data.map((d) => d[1] as number))
      : undefined;

    return {
      ...baseChartOptions,
      title: { text: title },
      yAxis: {
        ...(baseChartOptions.yAxis as Highcharts.YAxisOptions),
        title: { text: yLabel },
        ...(symBounds && { min: symBounds.min, max: symBounds.max }),
        plotLines: isDilation ? [{
          value: 0, color: '#BDBDBD', dashStyle: 'Dash' as Highcharts.DashStyleValue,
          width: 1, zIndex: 3,
        }] : undefined,
      },
      series: [
        {
          type: 'spline',
          name: title,
          data,
          color,
        },
        ...markers,
      ],
    };
  }, [readings, title, yLabel, yField, color, peakRow, residualRow]);
}
